import { ElNotification } from "element-plus";
import { computed, ref, Ref } from "vue";
import { Emitter } from "mitt";
import {
  ButtonConfig,
  useAdditionalAttrStore,
} from "../stores/AdditionalAttrStore";
import i18n from "../lang";
import { useConfiguratorStore } from "../stores/configuratorData";
import {
  copyClipboard,
  directLogout,
  isWindowKey,
  localeDateFormat,
  openFullLoading,
} from "../utils/tools";
import { resetConfigurator } from "./common";
import { GetDatasheet, GetResults, GetTechnicalEvaluation } from "../api/obj.api";
import { favoriteStore } from "../stores/favoriteStore";
import { useOverlayStore } from "../stores";
import { useLoginStore } from "../stores/LoginStore";
import { configuratorIdStore } from "../stores/configuratorIdStore";
import { EventListenerType } from "../utils/event";
import { PriceInformation } from "../stores/types";
import { useDataLayerStore } from "@/stores/DataLayerStore";

const inputQty = ref(0);
export async function onQuantityChanged(value: number | undefined) {
  if (value) {
    const dataConfig = useAdditionalAttrStore().getDataConfig;
    if (value < dataConfig.minQty || value > dataConfig.maxQty) {
      const { t } = i18n.global;
      ElNotification({
        title: "Error",
        message: t("error.amount"),
        type: "error",
      });
      return false;
    }
    isQuantityChanging.value = true;
    try {
      await useConfiguratorStore().setQuantity(value);
      inputQty.value = value;
    } finally {
      isQuantityChanging.value = false;
    }
  }
}

export const updateisQuantitychanging = (value: boolean) => {
  isQuantityChanging.value = value;
};

export const isQuantityChanging = ref(false);

export const isQuantityEnabled = computed(
  () => useConfiguratorStore().getCurrentState?.CodeComplete
);

export const isPrimaryButtonEnabled = computed(
  () => useConfiguratorStore().getCurrentState?.CodeComplete
);

export function checkGetDatasheetPermission(
  item: ButtonConfig,
  isPrimary?: boolean
) {
  const configuratorStore = useConfiguratorStore();

  if(item.disabled){
    return true;
  }

  if (
    item.disabledIfNotFeasible == true &&
    configuratorStore.getRequireTechnicalEvaluation == true &&
    configuratorStore.apiTechnicalEvaluation?.Result?.IsFeasible == false
  ) {
    return true;
  }
  if (item.disabledWhileIncomplete === false) {
    return false;
  }
  if (
    configuratorStore.getResultData?.ItemInformation == undefined &&
    isPrimary
  ) {
    return true;
  }

  if (configuratorStore.buttonIsLoading) {
    return true;
  }

  if (item.disabledWhileIncomplete) {
    return !configuratorStore.getCurrentState?.CodeComplete;
  }
  if (item?.callbackInternal === "getDatasheet") {
    if (configuratorStore.getSelectedProduct?.HasIbD === "false") {
      return true;
    }
  }
  return false;
}

export const quantity = computed(() => {
  return inputQty.value
    ? inputQty.value
    : useConfiguratorStore().getQuantity ??
        useAdditionalAttrStore().dataConfig.qty;
});

export const moreButtons = computed(() => {
  const moreOptionButtonActionsArr: Array<ButtonConfig> = [];
  useAdditionalAttrStore().options.moreOptionButtonActions?.filter((MOBA) => {
    if (
      MOBA.hiddenForNonEvaluatingModels == true &&
      !useConfiguratorStore().getRequireTechnicalEvaluation
    ) {
      return false;
    }
    return true;
  }).map((MOBA) => {
    if (MOBA.overwrites?.conditions) {
      let overwriteBtn = false;
      const DeliveryCategoryInt =
        useConfiguratorStore().resultData?.ItemInformation?.DeliveryInformation
          .DeliveryCategoryInt;

      let overwriteBtnIntCat = false;

      MOBA.overwrites?.conditions.deliveryCategories.map((xdeliveryCat) => {
        if (xdeliveryCat == DeliveryCategoryInt) {
          overwriteBtnIntCat = true;
        }
      });
      
      const hasValidTerminationExpected = MOBA.overwrites?.conditions.hasValidTermination ?? false;

      if (
        MOBA.overwrites?.conditions.isComplete == true &&
        useConfiguratorStore().getCurrentState?.CodeComplete == true &&
        hasValidTerminationExpected === !useConfiguratorStore().resultData?.ItemInformation?.DeliveryInformation
          ?.TerminationError &&
        overwriteBtnIntCat
      ) {
        overwriteBtn = true;
      }

      if (overwriteBtn) {
        moreOptionButtonActionsArr.push(MOBA.overwrites.values);
      } else {
        moreOptionButtonActionsArr.push(MOBA);
      }
    } else {
      moreOptionButtonActionsArr.push(MOBA);
    }
  });

  return moreOptionButtonActionsArr ?? [];
});

export const primaryButton = computed(() => {
  if (useAdditionalAttrStore().options.primaryButton?.overwrites?.conditions) {
    let overwriteBtn = false;
    const DeliveryCategoryInt =
      useConfiguratorStore().resultData?.ItemInformation?.DeliveryInformation
        .DeliveryCategoryInt;

    let overwriteBtnIntCat = false;

    useAdditionalAttrStore().options.primaryButton?.overwrites?.conditions.deliveryCategories.map(
      (x) => {
        if (x == DeliveryCategoryInt) {
          overwriteBtnIntCat = true;
        }
      }
    );
	
	const hasValidTerminationExpected = useAdditionalAttrStore().options.primaryButton?.overwrites?.conditions
        .hasValidTermination ?? false;

    // overwrites.conditions
    if (
      useAdditionalAttrStore().options.primaryButton?.overwrites?.conditions
        .isComplete == true &&
      useConfiguratorStore().getCurrentState?.CodeComplete == true &&
      hasValidTerminationExpected === !useConfiguratorStore().resultData?.ItemInformation?.DeliveryInformation
          ?.TerminationError &&
      overwriteBtnIntCat
    ) {
      overwriteBtn = true;
    }

    if (overwriteBtn) {
      return useAdditionalAttrStore().options.primaryButton?.overwrites
        ?.values as ButtonConfig;
    } else {
      return useAdditionalAttrStore().options.primaryButton;
    }
  } else {
    return useAdditionalAttrStore().options.primaryButton ?? {};
  }
});

const resetConfiguratorHandle = async () => {
  const loginStore = useLoginStore();
  const configuratorStore = useConfiguratorStore();
  const additionalAttrStore = useAdditionalAttrStore();
  const { t } = i18n.global;
  const loading = openFullLoading(false, t("text.loading"));
  try {
    await loginStore.logout();
    additionalAttrStore.clearCurrentModelCodeObj();
    // loginStore.$reset();
    configuratorStore.$reset();

    await loginStore.LoginToStore();
    await configuratorStore.initilizeConfigurator();
    await configuratorStore.setSelectedConfig();
  } finally {
    loading.close();
  }
};

function base64ToBlob(code: string) {
  code = code.replace(/[\n\r]/g, "");
  const raw = window.atob(code);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: "application/pdf" });
}

export const dialogShowQrcode = ref(false);
export const qrCodeStr = ref("");
export const buildCodeInput = ref("");
export const shareCodeExpairText = ref("");

export function copyQrCodeHandle() {
  if (buildCodeInput.value !== "") copyClipboard(buildCodeInput.value);
}

export const priceInformation: Ref<PriceInformation> = computed(() => {
  const configuratorStore = useConfiguratorStore();
  const defaultDeliveryDate = "-";
  const { t } = i18n.global;

  const result = configuratorStore.resultData;
  if (configuratorStore.getCurrentState?.CodeComplete && result) {
    const deliveryInfo = result.ItemInformation?.DeliveryInformation;
    const pricing = result.ItemInformation?.Pricing;

    const deliveryTime = Number(deliveryInfo?.DeliveryTime);
    const attr = useAdditionalAttrStore();

    let readyForDispatch;
    if (deliveryTime == 0) {
      readyForDispatch = t("label.Immediately");
    } else if (deliveryTime == 1) {
      readyForDispatch = deliveryTime + " " + t("label.Workingday");
    } else if (deliveryTime == 2) {
      readyForDispatch = deliveryTime + " " + t("label.Workingdays");
    } else if (deliveryTime > 2) {
      readyForDispatch = deliveryTime + " " + t("label.Workingdays");
    } else {
      readyForDispatch = deliveryTime + " " + t("label.Workingdays");
    }

    const expDeliveryDate = deliveryInfo?.DeliveryDateStd
      ? localeDateFormat(deliveryInfo?.DeliveryDateStd)
      : defaultDeliveryDate;

    const stockOnHand =
      Number(result.ItemInformation?.Stock.AvailableQty).toFixed(0) +
      " " +
      result.ItemInformation?.Stock.Unit;

    const totalPrice = new Intl.NumberFormat(attr.getlangValue, {
      style: "currency",
      currency: pricing?.Currency,
    }).format(Number(pricing?.PositionPrice));

    const unitPrice = new Intl.NumberFormat(attr.getlangValue, {
      style: "currency",
      currency: pricing?.Currency,
    }).format(Number(pricing?.NetPrice));

    let additionalPrice = "0";
    let showAdditionalPrice = false;

    if (Number(pricing?.AdditionsValue) > 0) {
      showAdditionalPrice = true;
      additionalPrice = new Intl.NumberFormat(attr.getlangValue, {
        style: "currency",
        currency: pricing?.Currency,
      }).format(Number(pricing?.AdditionsValue));
    }

    return {
      readyForDispatch,
      stockOnHand,
      totalPrice,
      unitPrice,
      expDeliveryDate,
      additionalPrice,
      showAdditionalPrice,
    };
  } else {
    const zeroPrice = "-";
    return {
      readyForDispatch: t("label.notAvailable"),
      expDeliveryDate: t("label.notAvailable"),
      stockOnHand: "-",
      unitPrice: zeroPrice,
      totalPrice: zeroPrice,
      additionalPrice: zeroPrice,
      showAdditionalPrice: false,
    };
  }
});

export async function handleShareQrcode() {
  const configuratorStore = useConfiguratorStore();
  if (configuratorStore.buttonIsLoading) {
    return true;
  }
  const { t } = i18n.global;
  dialogShowQrcode.value = true;
  const idStore = configuratorIdStore();
  const res = await idStore.getConfiguratorId();
  if (typeof res === "string") {
    qrCodeStr.value = "";
  } else {
    if (res.data?.code) {
      buildCodeInput.value = res.data.code;
      qrCodeStr.value = res.data.code;
    }
    if (res.data?.expiration_date !== "") {
      shareCodeExpairText.value = t("header.dialog.shareCodeExpair").replace(
        /#DATE#/,
        localeDateFormat(res.data.expiration_date)
      );
    }
  }

  if (res == "error") {
    dialogShowQrcode.value = false;
  }
  return res;
}

const forCallback = {
  BuildId: "",
};

export async function handleResetConfigurator(
  eventBus: Emitter<EventListenerType>
) {
  const overlayStore = useOverlayStore();
  let detailFallback = null;
  const configuratorStore = useConfiguratorStore();
  overlayStore.setconfirmCloseCalled(false);

  await resetConfiguratorHandle();
  resetConfigurator.value = true;
  eventBus.emit("resetConfigurator", true);
  eventBus.emit("resetImageSelector", true);
  detailFallback = configuratorStore.getCurrentState;
  eventBus.emit("resetSelectedOptionalCertificate", true);
  if (detailFallback !== undefined && forCallback.BuildId !== "") {
    detailFallback.BuildId = forCallback.BuildId;
  }
  const handleEvent = new CustomEvent("resetOfConfigurator", {
    bubbles: true,
    detail: configuratorStore.getCurrentState,
  });
  document.dispatchEvent(handleEvent);
}

export async function handleButton(
  item: ButtonConfig,
  eventBus: Emitter<EventListenerType>,
  isPrimary?: boolean
): Promise<boolean | undefined> {
  const configuratorStore = useConfiguratorStore();
  const favoriteItems = favoriteStore();
  const overlayStore = useOverlayStore();
  const idStore = configuratorIdStore();
  let results = null;
  let resultInfo = null;
  let loading = null;
  let wrapLoading = null;

  const { t } = i18n.global;

  if (isPrimary) {
    useDataLayerStore().dataLayerAddToCart();
    useDataLayerStore().dataLayerConfiguratorToShop();
  }

  if (item.disabledWhileIncomplete) {
    if (!configuratorStore.getCurrentState?.CodeComplete) {
      return false;
    }
  }
  forCallback.BuildId = "";
  if (item.pregenerateBuildId) {
    const loading = openFullLoading(false, t("text.loading"));
    const res = await idStore.getConfiguratorId();
    loading.close();
    if (typeof res !== "string") {
      if (res.data?.code) {
        forCallback.BuildId = res.data.code;
      }
    }
  }

  if (item.forceGetResults) {
    wrapLoading = openFullLoading(false, t("text.loading"));
    overlayStore.disableHandleError();
    if (
      (!configuratorStore.getisGetResultsCalled &&
        !configuratorStore.getCurrentState?.CodeComplete) ||
      !configuratorStore.apiGetResults
    ) {
      resultInfo = await GetResults();
      configuratorStore.setApiGetResults(resultInfo);
      configuratorStore.setIsGetResultsCalled(true);
    }
    wrapLoading.close();
  }
  if (item.callbackExternal) {
    const callback: string | number = item.callbackExternal;
    results = configuratorStore.getResultData;
    const cs = configuratorStore.getCurrentState;

    if (configuratorStore.apiGetResults !== null) {
      results = Object.assign({}, results, {
        ItemInformation: JSON.parse(
          JSON.stringify(configuratorStore.apiGetResults.ItemInformation)
        ),
      });
    }

    if (callback === "addConfigurationToFavourites") {
      if (cs) {
        favoriteItems.addItem(cs);
        ElNotification({
          message: t("label.favoriteSuccess").replace("%1", cs.CurrentType),
          type: "success",
        });
        return true;
      }
    }

    if (
      isWindowKey(callback) &&
      typeof callback === "string" &&
      window[callback] !== undefined
    ) {
      if (wrapLoading == null) {
        loading = openFullLoading(false, t("text.loading"));
      } else {
        loading = wrapLoading;
      }
      if (item.callbackData) {
        window[callback](
          {
            ...cs,
            ...results,
            ...forCallback,
            callbackData: item.callbackData,
          },
          loading,
          directLogout
        );
      } else {
        window[callback](
          {
            ...cs,
            ...results,
            ...forCallback,
          },
          loading,
          directLogout
        );
      }
    }
  }
  if (item.callbackInternal) {
    const callback = item.callbackInternal;
    let detailFallback = null;

    if (callback === "helpModel" || callback === "helpModal") {
      overlayStore.showVisibleSupport();
      if (forCallback.BuildId !== "") {
        detailFallback = { BuildId: forCallback.BuildId };
      } else {
        detailFallback = true;
      }
      if (configuratorStore.apiGetResults !== null) {
        detailFallback = Object.assign({}, detailFallback, {
          resInfo: JSON.parse(
            JSON.stringify(configuratorStore.apiGetResults.ItemInformation)
          ),
        });
      }
      const handleEvent = new CustomEvent("showHelpMessage", {
        bubbles: true,
        detail: detailFallback,
      });
      document.dispatchEvent(handleEvent);
    }
    if (callback === "resetConfiguration") {
      overlayStore.setConfirmClose(true, "resetConfigurator", true);
    }
    if (callback === "handleShareQrcode") {
      detailFallback = await handleShareQrcode();
      if (configuratorStore.apiGetResults !== null) {
        detailFallback = Object.assign({}, detailFallback, {
          resInfo: JSON.parse(
            JSON.stringify(configuratorStore.apiGetResults.ItemInformation)
          ),
        });
      }
      eventBus.emit("pregenerateBuildId", true);
      const handleEvent = new CustomEvent("pregenerateBuildId", {
        bubbles: true,
        detail: detailFallback,
      });
      document.dispatchEvent(handleEvent);
    }
    if (callback === "getDatasheet") {
      const loading = openFullLoading(false, t("text.loading"));
      const res = await GetDatasheet("pdf");
      if (res.Status === "OK") {
        if (res.ItemBasedDataSheet?.Data) {
          const pdfBlob = base64ToBlob(res.ItemBasedDataSheet.Data);
          const downloadURL = window.URL.createObjectURL(pdfBlob);
          const tmpLink = document.createElement("a");
          tmpLink.download = "DataSheet.pdf";
          tmpLink.href = downloadURL;
          tmpLink.style.display = "none";
          document.body.appendChild(tmpLink);
          tmpLink.click();
          window.URL.revokeObjectURL(downloadURL);
          document.body.removeChild(tmpLink);
        }
      }
      loading.close();
      if (res.Status === "OK") {
        detailFallback = res.ItemBasedDataSheet;
        if (forCallback.BuildId !== "") {
          detailFallback.BuildId = forCallback.BuildId;
        }
        if (configuratorStore.apiGetResults !== null) {
          detailFallback = Object.assign({}, detailFallback, {
            resInfo: JSON.parse(
              JSON.stringify(configuratorStore.apiGetResults.ItemInformation)
            ),
          });
        }
        const handleEvent = new CustomEvent("getDatasheet", {
          bubbles: true,
          detail: detailFallback,
        });
        document.dispatchEvent(handleEvent);
      }
    }
    if (callback === "showTechnicalEvaluationDetails") {
      if(configuratorStore.getCurrentState?.CodeComplete) {
        await GetTechnicalEvaluation();
        eventBus.emit("showTechnicalEvaluationDetails", true);
      }
    }
  }
}

export const getSubMenuHeight = (len: number | undefined) => {
  const perLine = 30;
  if (len !== undefined) {
    if (len > 2) {
      return "90px";
    } else {
      return perLine * len + "px";
    }
  }
  return "30px";
};
